import React, { useState, useEffect } from 'react';
import {
  Modal, Row, Col, Input, Spin, Radio, message,
} from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { func, bool, string } from 'prop-types';
import { GET_ACTIVITY_SUBMISSION, UPDATE_ACTIVITY_SUBMISSION, GET_ACTIVITY_SUBMISSIONS } from '../../../graphql';

/**
 * A submission is embedded on the assignment record, so it is addressed by
 * the activity and its author rather than by an id of its own.
 */
const SubmissionDetailModal = ({
  setShowModal, showModal, activityId, userId,
}) => {
  const [record, setRecord] = useState(null);
  const [inputs, setInputs] = useState({ status: null, rating: null });
  const variables = { activityId, userId };
  const {
    loading: submissionLoading, data: submissionPayload,
  } = useQuery(GET_ACTIVITY_SUBMISSION, { variables });
  const [updateSubmission, { loading: updateLoading }] = useMutation(UPDATE_ACTIVITY_SUBMISSION);

  useEffect(() => {
    if (submissionLoading || !submissionPayload?.submission) return;
    const found = submissionPayload.submission;
    setInputs({
      status: found.submission?.status ?? null,
      rating: found.submission?.rating ?? null,
    });
    setRecord(found);
  }, [submissionLoading, submissionPayload]);

  const handleOk = async () => {
    const { status, rating } = inputs;
    if (!status) return setShowModal(false);
    try {
      await updateSubmission({
        variables: {
          activityId,
          userId,
          input: { status, rating: Number(rating) || 0 },
        },
        update: (cache, { data: { updateSubmission: graded } }) => {
          const cached = cache.readQuery({
            query: GET_ACTIVITY_SUBMISSIONS,
            variables: { activityId },
          });
          if (!cached) return;
          cache.writeQuery({
            query: GET_ACTIVITY_SUBMISSIONS,
            variables: { activityId },
            data: {
              submissions: cached.submissions.map(
                (row) => (row.userId === graded.userId ? { ...row, ...graded } : row),
              ),
            },
          });
        },
      });
      message.success(`Submission was ${status}`);
      return setShowModal(false);
    } catch (error) {
      return message.error(error.message || 'Error, could not update submission');
    }
  };

  const onChange = (e) => {
    const { value, name } = e.target;
    setInputs({ ...inputs, [name]: value });
  };
  return (
    <Modal
      title="Submission Details"
      style={{ top: 20 }}
      open={showModal}
      onOk={handleOk}
      onCancel={() => setShowModal(false)}
      maskClosable={false}
      confirmLoading={updateLoading}
    >
      <Row justify="center">
        {record?.submission && (
        <>
          <Col span={24}>
            <h5>Student Name</h5>
            <Input disabled value={`${record.user?.firstName ?? ''} ${record.user?.lastName ?? ''}`} />
          </Col>
          <Col span={24}>
            <h5 className="gx-mt-3">Text Content</h5>
            <Input.TextArea disabled value={record.submission.textContent ?? ''} />
          </Col>
          <Col className="gx-mb-3" span={24}>
            <h5 className="gx-mt-3">Attached Files</h5>
            <ol>
              {record.submission.attachments.map(({ url, filename }) => (
                <li key={url}><h5><a className="sub-link" rel="noopener noreferrer" target="_blank" download href={url}>{filename.slice(11)}</a></h5></li>
              ))}
            </ol>
            {record.submission.attachments.length === 0 && <h6>No file attached</h6>}
          </Col>
          <Col span={24}>
            <Radio.Group value={inputs.status} name="status" onChange={onChange}>
              <Radio value="ACCEPTED">Accept</Radio>
              <Radio value="REJECTED">Reject</Radio>
            </Radio.Group>
          </Col>
          {inputs.status === 'ACCEPTED' && (
            <Col className="gx-mt-3" span={24}>
              <Input prefix="% " type="number" onChange={onChange} value={inputs.rating} placeholder="Rating in %" name="rating" />
            </Col>
          )}
        </>
        )}
        {submissionLoading && <Spin className="gx-mt-3" />}
      </Row>
    </Modal>
  );
};

SubmissionDetailModal.propTypes = {
  setShowModal: func.isRequired,
  showModal: bool.isRequired,
  activityId: string.isRequired,
  userId: string.isRequired,
};

export default SubmissionDetailModal;
